'use client';

import Image from 'next/image';
import { motion, useTransform, type MotionValue } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { customRichTextTags } from '@/utils/i18n';
import { PatientCard } from './reanalysis-patient-card';
import { ReanalysisLink } from './reanalysis-content';
import { SummaryTypingText } from './reanalysis-summary';
import MobileSectionEclipse from './mobile-section-eclipse';
import {
  CONCLUSION_SETTLED,
  LINK_VISIBLE,
  REANALYSIS_SCENES,
  SCENE_START,
  SCENE_STARTS,
  SUMMARY_TYPING,
} from './reanalysis-timeline';

const richTags = { br: customRichTextTags.solidBr };
// question 은 데스크톱(reanalysis-animation)과 문구를 공유하되 끊는 지점이 달라, <br>은 여기서 무시한다
const questionRichTags = { br: () => null, mobileBr: () => <br /> };
const PATIENTS = [
  { key: 'inflammation', days: 12, x: -100, y: 420 },
  { key: 'hypotonia', days: 46, x: 340, y: -20 },
  { key: 'muscle', days: 389, x: 340, y: 420 },
  { key: 'development', days: 128, x: -100, y: -20 },
] as const;

// 모바일은 390×600 전용 구성이라, 데스크톱 캔버스를 축소해서 그리면
// 환자·진단 텍스트를 읽을 수 없게 된다.
export default function MobileReanalysis({
  progress,
}: {
  progress: MotionValue<number>;
}) {
  const t = useTranslations('Main.reanalysis');
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scene, setScene] = useState(0);
  const [showLink, setShowLink] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () =>
      setScale(Math.min(element.clientWidth / 390, element.clientHeight / 600));
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => observer.disconnect();
  }, []);

  // progress 는 부모(ScrollStage)의 렌더 도중에도 갱신되므로, 곧바로 상태를 바꾸면
  // 다른 컴포넌트를 렌더하는 중에 setState 하게 된다. 프레임당 한 번만 장면을 맞춘다.
  useEffect(() => {
    let frame: number | undefined;
    const apply = () => {
      frame = undefined;
      const value = progress.get();
      setScene(
        Math.max(
          0,
          SCENE_STARTS.findLastIndex(start => value >= start),
        ),
      );
      setShowLink(value >= LINK_VISIBLE);
    };
    const schedule = () => {
      if (frame === undefined) frame = requestAnimationFrame(apply);
    };
    apply();
    const unsubscribe = progress.on('change', schedule);
    return () => {
      unsubscribe();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [progress]);

  const background = useTransform(progress, [0.04, 0.12], [0, 1]);
  const question = useTransform(progress, [0.24, SCENE_START.focus], [1, 0]);
  const cardsY = useTransform(progress, [SCENE_START.patients, 0.25], [600, 0]);
  const cardsOpacity = useTransform(
    progress,
    [SCENE_START.patients, 0.2, SCENE_START.focus, 0.46],
    [0, 1, 1, 0],
  );
  const featuredOpacity = useTransform(
    progress,
    [SCENE_START.patients, 0.2],
    [0, 1],
  );
  const featuredY = useTransform(
    progress,
    [SCENE_START.patients, 0.25, SCENE_START.focus, 0.4],
    [700, 100, 100, 0],
  );
  const featuredScale = useTransform(
    progress,
    [SCENE_START.focus, 0.4],
    [0.5, 1],
  );
  const rotateY = useTransform(progress, [0.44, 0.52], [0, 180]);
  const pending = useTransform(progress, [0.59, SCENE_START.diagnosed], [1, 0]);
  const diagnosis = useTransform(
    progress,
    [SCENE_START.diagnosed, 0.615, 0.68, SCENE_START.summary],
    [0, 1, 1, 0],
  );
  const spinner = useTransform(
    progress,
    [SCENE_START.analyzing, SCENE_START.diagnosed],
    [0, 720],
  );
  const check = useTransform(
    progress,
    [SCENE_START.diagnosed, 0.638, 0.666],
    [0, 1.25, 1],
  );
  const panelTimes = [
    SCENE_START.summary,
    SUMMARY_TYPING.start,
    SCENE_START.conclusion,
    CONCLUSION_SETTLED,
  ];
  const width = useTransform(progress, panelTimes, [300, 315, 315, 780]);
  const height = useTransform(progress, panelTimes, [400, 460, 460, 1200]);
  const radius = useTransform(progress, panelTimes, [19, 14, 14, 0]);
  const summary = useTransform(progress, [0.73, SUMMARY_TYPING.start], [0, 1]);
  const titleY = useTransform(
    progress,
    [SCENE_START.conclusion, CONCLUSION_SETTLED],
    [105, 0],
  );
  const titleScale = useTransform(
    progress,
    [SCENE_START.conclusion, CONCLUSION_SETTLED],
    [0.72, 1],
  );
  const quote = useTransform(progress, [0.9, LINK_VISIBLE], [0, 1]);
  const button = useTransform(progress, [LINK_VISIBLE, 0.97], [0, 1]);
  const eclipse = useTransform(
    progress,
    [SCENE_START.conclusion, CONCLUSION_SETTLED],
    [0, 1],
  );

  return (
    // 배경과 마무리 암전은 헤더 뒤까지 올려야 한다(데스크톱 reanalysis-animation 과 같은 구성).
    // 안 그러면 헤더 라운드 코너 뒤로 흰 바탕이 비쳐 헤더가 직각으로 보인다.
    <div
      ref={ref}
      data-reanalysis-scene={REANALYSIS_SCENES[scene]}
      className="relative isolate h-[var(--scroll-stage-height)] bg-[#0d0e10] [clip-path:inset(calc(-1*var(--scroll-stage-top))_0_0)]"
    >
      <div className="absolute inset-x-0 bottom-0 top-[calc(-1*var(--scroll-stage-top))] overflow-hidden bg-[#0d0e10]">
        <motion.div
          className="absolute inset-0 bg-white"
          style={{ opacity: background }}
        >
          <Image
            src="/images/main/reanalysis/background.webp"
            alt=""
            fill
            sizes="100vw"
            className="scale-[1.105] object-cover opacity-60"
          />
        </motion.div>
      </div>
      <motion.div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 top-[calc(-1*var(--scroll-stage-top))] bg-[#0d0e10]"
        style={{ opacity: eclipse }}
      />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div
            className="relative h-[600px] w-[390px]"
            style={{ transform: `scale(${scale})` }}
          >
            <motion.h2
              style={{ opacity: question }}
              className="absolute inset-0 flex items-center justify-center text-center text-[24px] font-semibold leading-8 tracking-[-0.464px] text-white"
            >
              {t.rich('question', questionRichTags)}
            </motion.h2>
            <motion.div
              aria-hidden="true"
              style={{ y: cardsY, opacity: cardsOpacity }}
            >
              {PATIENTS.map(patient => (
                <div
                  key={patient.key}
                  className="absolute h-[420px] w-[300px] origin-top-left scale-50"
                  style={{ left: patient.x, top: patient.y }}
                >
                  <PatientCard patient={patient.key} days={patient.days} />
                </div>
              ))}
            </motion.div>
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 [perspective:1000px]"
            >
              <motion.div
                className="relative [transform-style:preserve-3d]"
                style={{
                  width,
                  height,
                  y: featuredY,
                  scale: featuredScale,
                  rotateY,
                  opacity: featuredOpacity,
                }}
              >
                <div className="absolute inset-0 overflow-hidden rounded-[19px] [backface-visibility:hidden]">
                  <div className="h-[640px] w-[480px] origin-top-left scale-[0.625]">
                    <PatientCard patient="cafe-au-lait" days={325} featured />
                  </div>
                </div>
                <motion.div
                  className="absolute inset-0 grid place-items-center bg-[#0d0e10] p-5 text-center text-[15px] font-medium leading-[23px] text-[#d4deee] [backface-visibility:hidden] [transform:rotateY(180deg)]"
                  style={{ borderRadius: radius }}
                >
                  <motion.div
                    className="col-start-1 row-start-1 flex flex-col items-center gap-3"
                    style={{ opacity: pending }}
                  >
                    <motion.div style={{ rotate: spinner }}>
                      <Image
                        src="/images/main/reanalysis/spinner.webp"
                        alt=""
                        width={23}
                        height={23}
                      />
                    </motion.div>
                    <p>{t('pending', { days: 325 })}</p>
                  </motion.div>
                  <motion.div
                    className="col-start-1 row-start-1 flex flex-col items-center gap-3"
                    style={{ opacity: diagnosis }}
                  >
                    <motion.div style={{ scale: check }}>
                      <Image
                        src="/images/main/icons/checkmark-48.svg"
                        alt=""
                        width={23}
                        height={23}
                      />
                    </motion.div>
                    <p>
                      {t.rich('diagnosis', {
                        ...richTags,
                        strong: chunks => (
                          <strong className="text-[#b6f8ff]">{chunks}</strong>
                        ),
                      })}
                    </p>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          <div
            className="relative h-[600px] w-[390px]"
            style={{ transform: `scale(${scale})` }}
          >
            <motion.div
              className="absolute left-[45px] top-[165px] flex w-[300px] flex-col items-center gap-6 text-center text-white"
              style={{ opacity: summary }}
              aria-hidden={scene < REANALYSIS_SCENES.indexOf('summary')}
            >
              <motion.h3
                className="w-full text-[24px] font-semibold leading-8 tracking-[-0.464px]"
                style={{ y: titleY, scale: titleScale }}
              >
                <SummaryTypingText progress={progress} />
              </motion.h3>
              <motion.blockquote
                className="text-[12px] font-medium leading-[18px] tracking-[0.016px] text-white/80"
                style={{ opacity: quote }}
              >
                <p>{t.rich('quote', { br: () => <> </> })}</p>
                <footer className="mt-[18px]">{t('author')}</footer>
              </motion.blockquote>
              <motion.div
                style={{ opacity: button }}
                inert={!showLink}
                aria-hidden={!showLink}
              >
                <ReanalysisLink />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ opacity: eclipse }}
      >
        <MobileSectionEclipse half="upper" />
      </motion.div>
    </div>
  );
}
